import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Row, Select, Table, Tooltip } from 'antd';
import moment from 'moment';

import { FileExcelOutlined, SearchOutlined } from '@ant-design/icons';

import TaskComment from '../../Components/Schedule/TaskComment';
import useReportCooldown from '../../Hooks/useReportCooldown';
import { exportOccurrenceManagementReportByEmail } from '../../Services/exportReportService';
import ExportTaskListEmailModal from '../TaskList/Components/ExportTaskListEmailModal';

import { fetchManagementFilterOptions, fetchManagementList } from './API/OccurrenceManagementApi';
import buildOccurrenceManagementColumns from './Components/OccurrenceManagementColumns';

import './OccurrenceManagementStyle.scss';

const { RangePicker } = DatePicker;

const EMPTY_FILTER_VALUES = {
  period: null,
  occurrenceTypeName: null,
  occurrence: null,
  dealId: null,
  cardTitle: null,
  dealClient: null,
};

function getDefaultPeriod() {
  const operationalToday = moment().add(-3, 'hour');
  return [operationalToday.clone().subtract(29, 'days'), operationalToday.clone()];
}

function isPeriodInverted(period) {
  if (!period?.[0] || !period?.[1]) {
    return false;
  }
  return period[1].isBefore(period[0], 'day');
}

function validatePeriod(_, period) {
  if (isPeriodInverted(period)) {
    return Promise.reject(new Error('A data fim não pode ser anterior à data início'));
  }
  return Promise.resolve();
}

function toCivilPeriodBounds(period) {
  const bounds = {};
  if (period?.[0]) {
    bounds.startDate = moment(period[0]).startOf('day').toISOString();
  }
  if (period?.[1]) {
    bounds.endDate = moment(period[1]).endOf('day').toISOString();
  }
  return bounds;
}

function trimOrOmit(value) {
  if (value === undefined || value === null) {
    return undefined;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? undefined : trimmed;
}

function resolveOccurrencePayload(occurrenceValue, occurrenceOptions) {
  if (occurrenceValue === undefined || occurrenceValue === null || occurrenceValue === '') {
    return {};
  }
  const selected = occurrenceOptions.find((item) => String(item.value) === String(occurrenceValue));
  if (!selected) {
    return typeof occurrenceValue === 'string'
      ? { occurrenceName: occurrenceValue.trim() }
      : { occurrenceId: occurrenceValue };
  }
  const payload = {};
  if (selected.id != null && selected.id !== '') {
    payload.occurrenceId = selected.id;
  }
  if (selected.name) {
    payload.occurrenceName = selected.name;
  }
  return payload;
}

function buildListPayload(formValues, occurrenceOptions) {
  const { period, occurrence, occurrenceTypeName, dealId, cardTitle, dealClient } = formValues;
  return {
    ...toCivilPeriodBounds(period),
    occurrenceTypeName: trimOrOmit(occurrenceTypeName),
    dealId: trimOrOmit(dealId),
    cardTitle: trimOrOmit(cardTitle),
    dealClient: trimOrOmit(dealClient),
    ...resolveOccurrencePayload(occurrence, occurrenceOptions),
  };
}

function mapTypeOptions(types) {
  return (types ?? [])
    .filter((item) => item?.name)
    .map(({ name }) => ({ label: name, value: name }));
}

function mapOccurrenceOptions(occurrences) {
  return (occurrences ?? []).map((item) => ({
    label: item.name,
    value: item.id ?? item.name,
    id: item.id,
    name: item.name,
    typeName: item.typeName,
  }));
}

function mapStringOptions(values) {
  const seen = new Set();
  return (values ?? [])
    .map((value) => String(value ?? '').trim())
    .filter((value) => {
      if (!value || seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    })
    .map((value) => ({ label: value, value }));
}

function collectFieldValues(rows, camelKey, pascalKey) {
  return (rows ?? []).map((row) => row?.[camelKey] ?? row?.[pascalKey]);
}

function buildFieldSelectOptions(apiValues, rows, camelKey, pascalKey, previousOptions) {
  return mapStringOptions([
    ...(previousOptions ?? []).map((item) => item?.value ?? item),
    ...(apiValues ?? []),
    ...collectFieldValues(rows, camelKey, pascalKey),
  ]);
}

function filterOccurrencesByType(occurrenceOptions, typeName) {
  if (!typeName) {
    return occurrenceOptions;
  }
  return occurrenceOptions.filter((item) => item.typeName === typeName);
}

function mapManagementRowToTimelineComment(row) {
  const occurrenceTypeName = row?.occurrenceTypeName || '';
  return {
    id: row.id,
    createDate: row.createDate,
    sourceType: row.sourceType || `${occurrenceTypeName}NewOccurrence`,
    title: row.title || (occurrenceTypeName ? `Ocorrência ${occurrenceTypeName}` : 'Ocorrência'),
    userName: row.userName,
    userAvatar: row.userAvatar,
    occurrence: row.occurrenceName,
    description: row.description,
  };
}

function OccurrenceManagementView() {
  const [form] = Form.useForm();
  const [defaultPeriod] = useState(getDefaultPeriod);
  const [filters, setFilters] = useState({});
  const [managementRows, setManagementRows] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [occurrenceOptions, setOccurrenceOptions] = useState([]);
  const [dealIdOptions, setDealIdOptions] = useState([]);
  const [cardTitleOptions, setCardTitleOptions] = useState([]);
  const [dealClientOptions, setDealClientOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExportEmailModalVisible, setIsExportEmailModalVisible] = useState(false);
  const [exportEmailLoading, setExportEmailLoading] = useState(false);
  const [exportEmail, setExportEmail] = useState('');
  const cooldownSeconds = useReportCooldown('occurrenceManagement');

  const selectedTypeName = Form.useWatch('occurrenceTypeName', form);
  const visibleOccurrenceOptions = filterOccurrencesByType(occurrenceOptions, selectedTypeName);

  const loadPage = async () => {
    setIsLoading(true);
    const filterOptions = await fetchManagementFilterOptions();
    const mappedOccurrences = mapOccurrenceOptions(filterOptions.occurrences);
    const payload = buildListPayload({ period: defaultPeriod }, mappedOccurrences);
    const rows = await fetchManagementList(payload);
    setTypeOptions(mapTypeOptions(filterOptions.types));
    setOccurrenceOptions(mappedOccurrences);
    setDealIdOptions(buildFieldSelectOptions(filterOptions.dealIds, rows, 'dealId', 'DealId'));
    setCardTitleOptions(
      buildFieldSelectOptions(filterOptions.cardTitles, rows, 'cardTitle', 'CardTitle')
    );
    setDealClientOptions(
      buildFieldSelectOptions(filterOptions.dealClients, rows, 'dealClient', 'DealClient')
    );
    setFilters(payload);
    setManagementRows(rows);
    setIsLoading(false);
  };

  useEffect(() => {
    loadPage();
  }, []);

  const handleFilterSubmit = async (formValues) => {
    const payload = buildListPayload(formValues, occurrenceOptions);
    setFilters(payload);
    setIsLoading(true);
    const rows = await fetchManagementList(payload);
    setManagementRows(rows);
    setDealIdOptions((previous) =>
      buildFieldSelectOptions(null, rows, 'dealId', 'DealId', previous)
    );
    setCardTitleOptions((previous) =>
      buildFieldSelectOptions(null, rows, 'cardTitle', 'CardTitle', previous)
    );
    setDealClientOptions((previous) =>
      buildFieldSelectOptions(null, rows, 'dealClient', 'DealClient', previous)
    );
    setIsLoading(false);
  };

  const handleClearFilters = () => {
    form.setFieldsValue(EMPTY_FILTER_VALUES);
  };

  const handleOccurrenceTypeChange = () => {
    form.resetFields(['occurrence']);
  };

  const handleOpenExportModal = () => {
    const userData = JSON.parse(localStorage.getItem('conecta__userData') || '{}');
    setExportEmail(userData.email || '');
    setIsExportEmailModalVisible(true);
  };

  const handleConfirmExportEmail = async (email) => {
    setExportEmailLoading(true);
    await exportOccurrenceManagementReportByEmail({ Filters: filters }, email);
    setIsExportEmailModalVisible(false);
    setExportEmailLoading(false);
  };

  const columns = buildOccurrenceManagementColumns();

  return (
    <>
      <Form
        form={form}
        disabled={isLoading}
        name="OccurrenceManagementFilter"
        onFinish={handleFilterSubmit}
        layout="vertical"
        autoComplete="off"
        initialValues={{ period: defaultPeriod }}
        className="occurrence-management-filters"
      >
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="period" label="Período" rules={[{ validator: validatePeriod }]}>
              <RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="occurrenceTypeName" label="Tipo de ocorrência">
              <Select
                options={typeOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecione"
                dropdownStyle={{ borderRadius: 16 }}
                onChange={handleOccurrenceTypeChange}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="occurrence" label="Ocorrência">
              <Select
                options={visibleOccurrenceOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecione"
                dropdownStyle={{ borderRadius: 16 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="dealId" label="ID do negócio">
              <Select
                options={dealIdOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecione"
                dropdownStyle={{ borderRadius: 16 }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="cardTitle" label="Título do treinamento">
              <Select
                options={cardTitleOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecione"
                dropdownStyle={{ borderRadius: 16 }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="dealClient" label="Cliente">
              <Select
                options={dealClientOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecione"
                dropdownStyle={{ borderRadius: 16 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              Buscar
            </Button>
          </Col>
          <Col>
            <Button type="default" htmlType="button" onClick={handleClearFilters}>
              Limpar
            </Button>
          </Col>
          <Col>
            <Tooltip
              title={
                cooldownSeconds > 0
                  ? `Aguarde ${cooldownSeconds}s`
                  : 'Exportar ocorrências por e-mail'
              }
            >
              <Button
                type="default"
                htmlType="button"
                icon={<FileExcelOutlined />}
                onClick={handleOpenExportModal}
                disabled={cooldownSeconds > 0}
              >
                Exportar
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </Form>

      <div className="occurrence-management-timeline">
        {managementRows.length > 0 ? (
          <TaskComment comments={managementRows.map(mapManagementRowToTimelineComment)} />
        ) : (
          !isLoading && (
            <p className="occurrence-management-timeline-empty">
              Nenhuma ocorrência encontrada para os filtros aplicados.
            </p>
          )
        )}
      </div>

      <Table
        columns={columns}
        dataSource={managementRows}
        loading={isLoading}
        rowKey="id"
        size="small"
        scroll={{ x: 1400, y: '60vh' }}
        className="occurrence-management-table"
        key={JSON.stringify(filters)}
        pagination={{
          position: ['bottomRight'],
          size: 'default',
          defaultPageSize: 10,
          pageSizeOptions: [10, 20, 30, 50, 100, 200],
          showSizeChanger: true,
          total: managementRows?.length ?? 0,
          showTotal: (total) => `${total} ocorrências`,
        }}
      />

      <ExportTaskListEmailModal
        isVisible={isExportEmailModalVisible}
        setIsVisible={setIsExportEmailModalVisible}
        onConfirm={handleConfirmExportEmail}
        defaultEmail={exportEmail}
        loading={exportEmailLoading}
      />
    </>
  );
}

export default OccurrenceManagementView;
